/* eslint-disable no-param-reassign */
import {
  createSlice,
  createEntityAdapter,
  EntityState,
  createSelector,
} from '@reduxjs/toolkit'
import { Selector } from 'react-redux'
import { RequestState } from 'src/domain/request'
// eslint-disable-next-line import/no-cycle
import { getCalculationDSLs } from 'src/slices/calculationDSLs/calculationDSLsSlice'
import {
  GetParams,
  getCalculationGroup,
  selectByParams as selectCalculationGroupsByParams,
} from 'src/slices/calculationGroups/calculationGroupsSlice'
import { CalculationItem } from 'src/slices/services/api'
import { RootState } from 'src/store'
import { purge } from 'src/store/action'

export interface CalculationItemState
  extends EntityState<CalculationItem>,
    RequestState {}

const calculationItemsAdapter = createEntityAdapter<CalculationItem>()

export const initialState: CalculationItemState =
  calculationItemsAdapter.getInitialState({
    status: 'idle',
  })

const slice = createSlice({
  name: 'calculationItems',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(purge, () => {
      return initialState
    })
    builder.addCase(getCalculationDSLs.pending, (state) => {
      return {
        ...state,
        status: 'loading',
      }
    })
    builder.addCase(getCalculationDSLs.fulfilled, (state, { payload }) => {
      calculationItemsAdapter.upsertMany(
        state,
        payload.dsls?.calculationItems ?? {}
      )
      state.status = 'succeeded'
    })
    builder.addCase(getCalculationDSLs.rejected, (state, { error }) => {
      return {
        ...state,
        status: 'failed',
        error,
      }
    })
    builder.addCase(getCalculationGroup.fulfilled, (state, { payload }) => {
      calculationItemsAdapter.upsertMany(state, payload.calculationItems ?? {})
    })
  },
})

export default slice.reducer

// Selector
export const {
  selectById: selectCalculationItemById,
  selectEntities: selectCalculationItemEntities,
  selectAll: selectAllCalculationItems,
} = calculationItemsAdapter.getSelectors<CalculationItemState>((state) => state)

export const selectCalculationItemsByParams = (
  params: GetParams
): Selector<RootState, CalculationItem[] | undefined> => {
  return createSelector(
    [
      (state) => selectCalculationGroupsByParams(params)(state),
      (state) => selectCalculationItemEntities(state.entities.calculationItems),
    ],
    (calculationGroup, calculationItemEntities) => {
      return calculationGroup?.calculationItems
        .map((calculationItemId) => calculationItemEntities[calculationItemId])
        .filter(
          (calculationItem): calculationItem is CalculationItem =>
            calculationItem !== undefined
        )
    }
  )
}
